import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Platform,
  Switch,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import DateTimePicker from '../components/DateTimePicker';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { createTournament as createTournamentSupabase } from '../services/database';

const TournamentWizardScreen = ({ navigation, route }) => {
  const { clubId: propClubId } = route.params || {};
  const { user, appUser } = useAuth();
  const { showToast } = useToast();

  const [clubProfile, setClubProfile] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'SWISS',
    totalRounds: '5',
    allowPhones: true,
    startDate: new Date(),
    timeControl: '10+0',
    entryFee: '',
    isPaid: false,
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const profile = appUser;
      setClubProfile(profile);

      if (!profile?.claimedBy) {
        showToast('Claim your club before running tournaments', 'error');
        navigation.goBack();
      } else if (profile.tier !== 'PRO') {
        showToast('Tournament tools require Pro tier', 'error');
        navigation.navigate('UpgradeClub', { clubId: profile.id });
      }
    } catch (error) {
      console.error('Error loading club profile:', error);
      showToast('Failed to load club profile', 'error');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showToast('Please enter a tournament name', 'error');
      return;
    }
    if (form.isPaid && (!form.entryFee || parseFloat(form.entryFee) <= 0)) {
      showToast('Paid tournaments require a price greater than $0', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const input = {
        clubId: propClubId || clubProfile?.id,
        name: form.name.trim(),
        description: form.description.trim(),
        type: form.type,
        status: 'DRAFT',
        totalRounds: parseInt(form.totalRounds, 10) || 5,
        allowPhones: form.allowPhones,
        timeControl: form.timeControl,
        startTime: form.startDate.toISOString(),
        isPaid: form.isPaid,
        entryFee: form.isPaid && form.entryFee ? parseFloat(form.entryFee) : null,
        formatConfig: JSON.stringify({
          pairing: form.type,
          allowPhones: form.allowPhones,
          timeControl: form.timeControl,
        }),
      };

      const tournamentData = {
        club_id: input.clubId,
        name: input.name,
        description: input.description,
        type: input.type,
        status: input.status,
        total_rounds: input.totalRounds,
        allow_phones: input.allowPhones,
        time_control: input.timeControl,
        start_time: input.startTime,
        is_paid: input.isPaid,
        entry_fee: input.entryFee,
        format_config: input.formatConfig,
      };
      const result = await createTournamentSupabase(tournamentData);

      showToast('Tournament created! Finish setup in control panel.', 'success');
      navigation.replace('TournamentDetail', { tournamentId: result.id });
    } catch (error) {
      console.error('Error creating tournament:', error);
      showToast('Failed to create tournament', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <View style={styles.stepIndicator}>
      {[1, 2, 3].map((index) => (
        <View key={index} style={styles.stepWrapper}>
          <View style={[styles.stepCircle, step === index && styles.stepCircleActive]}>
            <Text style={styles.stepText}>{index}</Text>
          </View>
          {index < 3 && <View style={styles.stepLine} />}
        </View>
      ))}
    </View>
  );

  const renderStepOne = () => (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>Basic Details</Text>
      <View style={styles.field}>
        <Text style={styles.label}>Tournament Name</Text>
        <TextInput
          style={styles.input}
          value={form.name}
          onChangeText={(text) => handleInputChange('name', text)}
          placeholder="e.g., Downtown Blitz Open"
        />
      </View>
      <View style={styles.field}>
        <Text style={styles.label}>Description</Text>
        <TextInput
          style={[styles.input, styles.multiline]}
          value={form.description}
          onChangeText={(text) => handleInputChange('description', text)}
          placeholder="Share event highlights, prizes, etc."
          multiline
        />
      </View>
      <View style={styles.row}>
        <View style={[styles.field, { flex: 1 }]}>
          <Text style={styles.label}>Format</Text>
          <View style={styles.pillGroup}>
            {['SWISS', 'ROUND_ROBIN', 'ARENA'].map((type) => (
              <TouchableOpacity
                key={type}
                style={[styles.pill, form.type === type && styles.pillActive]}
                onPress={() => handleInputChange('type', type)}
              >
                <Text style={[styles.pillText, form.type === type && styles.pillTextActive]}>
                  {type.replace('_', ' ')}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
      <View style={styles.row}>
        <View style={[styles.field, { flex: 1 }]}>
          <Text style={styles.label}>Total Rounds</Text>
          <TextInput
            style={styles.input}
            value={form.totalRounds}
            keyboardType="numeric"
            onChangeText={(text) => handleInputChange('totalRounds', text)}
          />
        </View>
        <View style={[styles.field, { flex: 1 }]}>
          <Text style={styles.label}>Time Control</Text>
          <TextInput
            style={styles.input}
            value={form.timeControl}
            onChangeText={(text) => handleInputChange('timeControl', text)}
            placeholder="e.g., 10+0"
          />
        </View>
      </View>
    </View>
  );

  const renderStepTwo = () => (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>Scheduling & Access</Text>
      <View style={styles.field}>
        <Text style={styles.label}>Start Date</Text>
        <TouchableOpacity
          style={styles.dateButton}
          onPress={() => setShowDatePicker(true)}
        >
          <Icon name="event" size={20} color="#666" />
          <Text style={styles.dateText}>
            {form.startDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
          </Text>
        </TouchableOpacity>
        {showDatePicker && (
          <DateTimePicker
            value={form.startDate}
            mode="date"
            display="default"
            onChange={(_, date) => {
              setShowDatePicker(false);
              if (date) {
                handleInputChange('startDate', new Date(date));
              }
            }}
          />
        )}
      </View>
      <View style={styles.field}>
        <Text style={styles.label}>Start Time</Text>
        <TouchableOpacity
          style={styles.dateButton}
          onPress={() => setShowTimePicker(true)}
        >
          <Icon name="schedule" size={20} color="#666" />
          <Text style={styles.dateText}>
            {form.startDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </TouchableOpacity>
        {showTimePicker && (
          <DateTimePicker
            value={form.startDate}
            mode="time"
            display="default"
            onChange={(_, date) => {
              setShowTimePicker(false);
              if (date) {
                const newDate = new Date(form.startDate);
                newDate.setHours(date.getHours());
                newDate.setMinutes(date.getMinutes());
                handleInputChange('startDate', newDate);
              }
            }}
          />
        )}
      </View>
      <View style={styles.switchRow}>
        <View>
          <Text style={styles.label}>Allow player devices</Text>
          <Text style={styles.helperText}>
            When disabled, players must report results to the director table.
          </Text>
        </View>
        <Switch
          value={form.allowPhones}
          onValueChange={(value) => handleInputChange('allowPhones', value)}
        />
      </View>
      <View style={styles.switchRow}>
        <View>
          <Text style={styles.label}>Paid entry</Text>
          <Text style={styles.helperText}>
            Enable Stripe payments for tournament entry fees.
          </Text>
        </View>
        <Switch
          value={form.isPaid}
          onValueChange={(value) => handleInputChange('isPaid', value)}
        />
      </View>
      {form.isPaid && (
        <View style={styles.field}>
          <Text style={styles.label}>Entry Fee (USD)</Text>
          <TextInput
            style={styles.input}
            keyboardType="decimal-pad"
            value={form.entryFee}
            onChangeText={(text) => handleInputChange('entryFee', text)}
          />
          <Text style={styles.helperText}>
            Payments use Stripe Connect. Platform fee applies to each entry and requires Pro tier.
          </Text>
        </View>
      )}
    </View>
  );

  const renderStepThree = () => (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>Review & Launch</Text>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Tournament</Text>
        <Text style={styles.summaryValue}>{form.name || 'Not set'}</Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Format</Text>
        <Text style={styles.summaryValue}>{form.type.replace('_', ' ')}</Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Rounds</Text>
        <Text style={styles.summaryValue}>{form.totalRounds}</Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Start</Text>
        <Text style={styles.summaryValue}>
          {form.startDate.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Player devices</Text>
        <Text style={styles.summaryValue}>
          {form.allowPhones ? 'Enabled' : 'Disabled'}
        </Text>
      </View>
      <View style={styles.summaryRow}>
        <Text style={styles.summaryLabel}>Entry fee</Text>
        <Text style={styles.summaryValue}>
          {form.isPaid ? `$${parseFloat(form.entryFee || '0').toFixed(2)}` : 'Free'}
        </Text>
      </View>
      <View style={styles.noticeBox}>
        <Icon name="info" size={18} color="#FF9800" />
        <Text style={styles.noticeText}>
          After creation you’ll unlock director controls: pairings, standings, projector mode,
          result collection, and rating updates.
        </Text>
      </View>
    </View>
  );

  const renderNavigation = () => (
    <View style={styles.navRow}>
      {step > 1 ? (
        <TouchableOpacity style={styles.backButton} onPress={() => setStep(step - 1)}>
          <Text style={styles.backButtonText}>Back</Text>
        </TouchableOpacity>
      ) : (
        <View style={{ width: 100 }} />
      )}
      {step < 3 ? (
        <TouchableOpacity style={styles.nextButton} onPress={() => setStep(step + 1)}>
          <Text style={styles.nextButtonText}>Next</Text>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          style={[styles.nextButton, submitting && styles.nextButtonDisabled]}
          onPress={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <Text style={styles.nextButtonText}>Create Tournament</Text>
          )}
        </TouchableOpacity>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={styles.centered}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerBack}>
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Tournament Wizard</Text>
        <View style={{ width: 24 }} />
      </View>
      {renderStepIndicator()}
      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {step === 1 && renderStepOne()}
        {step === 2 && renderStepTwo()}
        {step === 3 && renderStepThree()}
        <View style={{ height: 40 }} />
      </ScrollView>
      {renderNavigation()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 12,
  },
  headerBack: {
    padding: 6,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  content: {
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 18,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: Platform.OS === 'ios' ? 0.08 : 0.2,
    shadowRadius: 10,
    elevation: 4,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
    color: '#333',
  },
  field: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
    color: '#555',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    backgroundColor: '#fff',
    color: '#333',
  },
  multiline: {
    height: 90,
    textAlignVertical: 'top',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  pillGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  pill: {
    borderWidth: 1,
    borderColor: '#ddd',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: '#fff',
  },
  pillActive: {
    backgroundColor: '#E8F5E9',
    borderColor: '#94EB98',
  },
  pillText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#555',
  },
  pillTextActive: {
    color: '#2E7D32',
  },
  dateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 10,
    padding: 12,
  },
  dateText: {
    fontSize: 15,
    color: '#333',
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  helperText: {
    fontSize: 12,
    color: '#777',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  summaryLabel: {
    fontSize: 15,
    color: '#666',
  },
  summaryValue: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  noticeBox: {
    marginTop: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFF3E0',
    padding: 12,
    borderRadius: 10,
  },
  noticeText: {
    flex: 1,
    fontSize: 13,
    color: '#8D6E63',
  },
  stepIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  stepWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: '#ddd',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
  },
  stepCircleActive: {
    borderColor: '#94EB98',
    backgroundColor: '#E8F5E9',
  },
  stepText: {
    fontWeight: '600',
    color: '#333',
  },
  stepLine: {
    width: 50,
    height: 2,
    backgroundColor: '#ddd',
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    backgroundColor: '#fff',
  },
  backButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  backButtonText: {
    fontSize: 15,
    color: '#555',
  },
  nextButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: '#94EB98',
  },
  nextButtonDisabled: {
    opacity: 0.6,
  },
  nextButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 10,
    color: '#666',
  },
});

export default TournamentWizardScreen;

